import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';

import CopyField from '@/common/forms/CopyField';
import { showToast } from '@/common/showToast';
import TableNativeTokenBalanceBalance from '@/components/TableNativeTokenBalance';
import PlayerNativeTokenBalance from '@/components/PlayerNativeTokenBalance';
import { config } from '@repo/common-types';
import routes from '@/routes/routes-enums';
import {
  deletePlayerContractRest,
  deletePlayersContractRest,
  fetchTableByIdSessions,
  getPlayersContractRest,
} from '@/services/table/table-service';
import { useRefundTableMutation, useRecoverTableMutation } from '@/redux/api/table';
import { Session } from '@/services/table/table-types';
import PlayerStack from '@/components/PlayerStack';
import { TableDetailsDto, UserDto } from '@repo/common-types';

import TransactionsSection from './TransactionsSection';

interface RemovePlayerProps extends KickPlayerProps {
  tableContractId: number | null;
}

interface KickPlayerProps {
  smartWalletAddress: string;
  alias: string;
  tableId: string;
  contractAddress: string | null;
  removing: boolean;
  setRemoving: (status: boolean) => void;
  playersContracts: UserDto[];
  setPlayersContracts?: React.Dispatch<React.SetStateAction<UserDto[]>>;
  refetchPlayersContracts: () => void;
}

const KickPlayerSuccessToast = ({ txHash }: { txHash: string }) => {
  return (
    <div>
      <p>Player kicked successfully.</p>
      <CopyField
        className="w-100"
        name="txHash"
        label={false}
        value={txHash}
        link={`${config.deposits.polygon.blockExplorerUrl}/tx/${txHash}`}
      />
    </div>
  );
};

const KickPlayer: React.FC<KickPlayerProps> = ({
  smartWalletAddress,
  alias,
  tableId,
  removing,
  setRemoving,
  playersContracts,
  refetchPlayersContracts,
}) => {
  const [kickPlayerConfirmOpen, setKickPlayerConfirmOpen] = useState(false);
  const toggle = () => setKickPlayerConfirmOpen(!kickPlayerConfirmOpen);

  const kickPlayer = (tableId: string, address: string) => {
    setRemoving(true);
    deletePlayerContractRest(tableId, null, address)
      .then((response) => {
        toast.success(
          <div>
            <p>Player kicked successfully.</p>
          </div>,
          {
            autoClose: 2000,
            closeOnClick: true,
            hideProgressBar: true,
          },
        );
        const newPlayerContracts = playersContracts.filter((player) => player.smartWalletAddress !== address);
        // setPlayersContracts(newPlayerContracts);
        refetchPlayersContracts();
      })
      .catch((_error) => {
        showToast('Error when removing players', 'warning');
      })
      .finally(() => {
        setRemoving(false);
      });
  };
  return (
    <div className="d-flex flex-row gap-3" style={{ alignItems: 'center' }}>
      <div className="d-flex flex-row" style={{ alignItems: 'center' }}>
        <Button className="btn btn-sm btn-secondary mx-2" onClick={toggle} disabled={removing}>
          {removing ? <Spinner size="sm" /> : 'Kick'}
        </Button>
        <Modal isOpen={kickPlayerConfirmOpen} toggle={toggle} id="confirm-kick-player" centered>
          <ModalHeader toggle={toggle}>{`Are you sure you want to kick ${alias}?`}</ModalHeader>
          <ModalBody>If the player is playing, the player will be kicked after the round finishes.</ModalBody>
          <ModalFooter>
            <Button color="grey" onClick={() => setKickPlayerConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => kickPlayer(tableId, smartWalletAddress)} disabled={removing}>
              <i className="ri-error-warning-fill me-2"></i>
              Confirm
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

const RemovePlayer: React.FC<RemovePlayerProps> = ({
  smartWalletAddress,
  tableId,
  tableContractId,
  contractAddress,
  removing,
  setRemoving,
  playersContracts,
  refetchPlayersContracts,
}) => {
  const [amount, setAmount] = useState<string>('0');

  const removePlayer = (tableId: string, amount: string | null, address: string) => {
    setRemoving(true);
    deletePlayerContractRest(tableId, amount, address)
      .then((response) => {
        if (typeof response !== 'string') {
          throw new Error('error deleting contract player');
        }

        toast.success(<KickPlayerSuccessToast txHash={response} />, {
          pauseOnHover: true,
          autoClose: false,
          closeOnClick: false,
          hideProgressBar: true,
        });
        const newPlayerContracts = playersContracts.filter((player) => player.smartWalletAddress !== address);
        refetchPlayersContracts();
        // setPlayersContracts(newPlayerContracts);
      })
      .catch((_error) => {
        showToast('Error when removing players', 'warning');
      })
      .finally(() => {
        setRemoving(false);
      });
  };
  return (
    <div className="d-flex flex-row gap-3" style={{ alignItems: 'center' }}>
      <div className="d-flex flex-row" style={{ alignItems: 'center' }}>
        <PlayerNativeTokenBalance
          tableContractId={tableContractId}
          smartWalletAddress={smartWalletAddress}
          contractAddress={contractAddress}
          tokenSymbol="USDT"
          decimals={config.blockchain.usdtDecimals}
        />
        <span className="mx-2">USDT</span>
      </div>

      <div className="d-flex flex-row" style={{ alignItems: 'center' }}>
        <input type="number" className="form-control" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <button
          className="btn btn-sm btn-secondary mx-2"
          onClick={() => removePlayer(tableId, amount, smartWalletAddress)}
          disabled={removing}
        >
          {removing ? <Spinner size="sm" /> : 'Remove'}
        </button>
      </div>
    </div>
  );
};

const AddFundsTable = ({ tableId }: { tableId: string }) => {
  const [amount, setAmount] = useState<string | undefined>(undefined);
  const [refundTable] = useRefundTableMutation<{
    meta: {
      toast: {
        success: string;
        error: string;
      };
    };
  }>();

  const handleSubmit = async () => {
    if (!amount) {
      return;
    }
    refundTable({
      tableId,
      amount,
      meta: {
        toast: {
          success: 'Refund success',
          error: 'Failed to add funds',
        },
      },
    });
    setAmount('');
  };

  return (
    <tr>
      <th className="w-50">Add Funds</th>
      <td className="w-50">
        <input
          className="p-2"
          placeholder="Amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value.toString())}
        />
        <button className="btn btn-sm btn-secondary mx-2 p-2" onClick={handleSubmit}>
          Add
        </button>
      </td>
    </tr>
  );
};

const RemoveFundsTable = ({ tableId }: { tableId: string }) => {
  const [amount, setAmount] = useState<string | undefined>(undefined);
  const [recoverTable, { isLoading: submitting }] = useRecoverTableMutation();

  const handleSubmit = async () => {
    if (!amount) {
      return;
    }
    await recoverTable({
      tableId,
      amount,
      meta: {
        toast: {
          success: 'Funds removed successfully',
          error: 'Failed to remove funds',
        },
      },
    });
    setAmount(undefined);
  };

  return (
    <tr>
      <th className="w-50">Remove funds</th>
      <td className="w-50">
        <input
          className="p-2"
          placeholder="Amount"
          type="number"
          value={amount || ''}
          onChange={(e) => setAmount(e.target.value.toString())}
        />
        <button className="btn btn-sm btn-secondary mx-2 p-2" onClick={handleSubmit}>
          {submitting ? <Spinner size="sm" /> : 'Remove'}
        </button>
      </td>
    </tr>
  );
};

interface TableDetailsGameProps {
  table: TableDetailsDto;
  isStudGame: boolean;
  isLimitGame: boolean;
  refreshTable?: () => void;
}

const TableDetailsGame: React.FC<TableDetailsGameProps> = ({
  refreshTable,
  table,
  isStudGame: _isStudGame,
  isLimitGame: _isLimitGame,
}) => {
  const { tableId } = useParams<{ tableId: string }>();
  const navigate = useNavigate();
  const [removing, setRemoving] = useState<boolean>(false);

  const openUserLogs = (userId: string) => {
    if (!userId) return;
    const base = 'https://telemetry.betterstack.com/team/t238083/tail?s=969735,969739,969749';
    const query = encodeURIComponent(`metadata.tableId=${table.id} metadata.userId=${userId}`);
    const url = `${base}&q=${query}&rf=now-7d`;
    window.open(url, '_blank');
  };

  const {
    data: playersContracts,
    isLoading: isPlayersContractsLoading,
    refetch: refetchPlayersContracts,
  } = useQuery({
    queryKey: ['playersContracts', table.id],
    queryFn: () => getPlayersContractRest(table.id),
  });

  const { data: sessions, isLoading: isSessionsLoading } = useQuery({
    queryKey: ['sessions', table.id],
    queryFn: () => fetchTableByIdSessions(table.id),
  });

  const removePlayers = () => {
    if (!tableId) return;
    setRemoving(true);
    deletePlayersContractRest(tableId)
      .then(() => {
        showToast('Players removed from table', 'success');
        getPlayersContractRest(tableId)
          .then((response) => {
            if (Array.isArray(response)) refetchPlayersContracts();
          })
          .catch((_error) => {
            showToast('Error when fetching contract players', 'warning');
          });
      })
      .catch((_error) => {
        showToast('Error when removing players', 'warning');
      })
      .finally(() => {
        setRemoving(false);
      });
  };

  return (
    <div className="w-full">
      <h3>Table Summary</h3>
      <table className="table-hover table-centered table-nowrap mb-5 table align-middle">
        <tbody>
          <tr>
            <th className="w-50">Uncollected Rake</th>
            <td className="w-50">${table?.uncollectedRake?.toFixed(2)}</td>
          </tr>
          <tr>
            <th className="w-50">Table Stack</th>
            <td className="w-50">
              ${table?.seats?.reduce((acc, seat) => acc + seat.stack + (seat?.extraStack || 0), 0) || 0}
            </td>
          </tr>
          <tr>
            <th>Hands dealt</th>
            <td>{table?.handsDealt}</td>
          </tr>
          <tr>
            <th>Average Pot</th>
            <td>${table?.avgPot}</td>
          </tr>
          {table && (
            <>
              <tr>
                <th>Contract Id: {table.contractId} </th>
                <td>
                  <CopyField
                    label={false}
                    name="Table Contract"
                    value={table.contractAddress}
                    link={`${config.deposits.polygon.blockExplorerUrl}/address/${table.contractAddress}`}
                  />
                </td>
              </tr>
              <tr>
                <th>Balance in Contract</th>
                <td>
                  <TableNativeTokenBalanceBalance
                    tableContractId={table.contractId}
                    contractAddress={table.contractAddress}
                    tokenSymbol="USDT"
                    decimals={config.blockchain.usdtDecimals}
                  />{' '}
                  USDT
                </td>
              </tr>
              {playersContracts?.length && playersContracts?.length > 0 && (
                <tr>
                  <th>Players in Contract</th>
                  <td>
                    <span className="mx-2">{playersContracts?.length}</span>
                    <button className="btn btn-sm btn-primary mx-2" onClick={() => removePlayers()} disabled={true}>
                      {removing ? <Spinner size="sm" /> : 'Remove Players'}
                    </button>
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>

      {playersContracts?.length ? (
        <>
          <h3>Contract Players</h3>
          <table
            className="table-hover table-centered table-nowrap mb-5 table align-middle"
            style={{ borderTop: '2px solid #000' }}
          >
            <thead>
              <tr>
                <th>Address</th>
                <th>Alias</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {playersContracts.map((player: UserDto) => (
                <tr key={player.id}>
                  <td>{player.smartWalletAddress}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <span>{player.alias}</span>
                      <span>
                        <button
                          className="btn btn-sm btn-outline-secondary p-1"
                          style={{ minWidth: '32px' }}
                          title="Open user logs"
                          aria-label="Open user logs"
                          onClick={() => openUserLogs(player.id)}
                        >
                          <i className="ri-file-list-3-line"></i>
                        </button>
                      </span>
                    </div>
                  </td>
                  <td>
                    {player.smartWalletAddress && (
                      <RemovePlayer
                        alias={player.alias}
                        smartWalletAddress={player.smartWalletAddress}
                        tableId={tableId!}
                        tableContractId={table.contractId}
                        contractAddress={table.contractAddress}
                        removing={removing}
                        setRemoving={setRemoving}
                        playersContracts={playersContracts}
                        refetchPlayersContracts={refetchPlayersContracts}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {table?.seats && (
        <>
          <h3>Players Summary</h3>
          <table
            className="table-hover table-centered table-nowrap mb-5 table align-middle"
            style={{ borderTop: '2px solid #000' }}
          >
            <thead>
              <tr>
                <th>Seat #</th>
                <th>Alias</th>
                <th>Stack</th>
                <th>Address</th>
              </tr>
            </thead>
            <tbody>
              {table?.seats?.map((seat) => {
                return (
                  <tr key={seat.seat}>
                    <td>{seat.seat}</td>
                    <td>{seat.user?.alias}</td>
                    <td>{seat.stack + (seat.extraStack || 0)}</td>
                    <td>{seat?.user?.smartWalletAddress}</td>
                    {playersContracts?.find((p) => p.id === seat.userId) && (
                      <td>
                        {seat.user.smartWalletAddress && (
                          <KickPlayer
                            contractAddress={table.contractAddress}
                            smartWalletAddress={seat.user.smartWalletAddress}
                            alias={seat.user.alias}
                            tableId={tableId!}
                            removing={removing}
                            setRemoving={setRemoving}
                            playersContracts={playersContracts}
                            refetchPlayersContracts={refetchPlayersContracts}
                          />
                        )}{' '}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      <h3>Sessions</h3>
      <table
        className="table-hover table-centered table-nowrap mb-5 table align-middle"
        style={{ borderTop: '2px solid #000' }}
      >
        <thead>
          <tr>
            <th>User</th>
            <th>Stack</th>
            <th>Contract Balance</th>
            <th>Hands Dealt</th>
            <th>Created At</th>
            <th>Closed At</th>
          </tr>
        </thead>
        <tbody>
          {sessions?.map((session: Session) => (
            <tr key={session.id}>
              <td>
                <span onClick={() => navigate(`${routes.USER_EDIT}/${session.userId}`)}>{session.user.alias}</span>
              </td>
              <td>{session.stack}</td>
              <td>
                {session.user.smartWalletAddress && table.contractId && (
                  <PlayerStack
                    tableContractId={table.contractId}
                    contractAddress={table.contractAddress}
                    userWallet={session.user.smartWalletAddress}
                    tokenSymbol="USDT"
                    decimals={config.blockchain.usdtDecimals}
                  />
                )}
              </td>
              <td>{session.handsDealt}</td>
              <td>{session.createdAt}</td>
              <td>{session.closedAt}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <TransactionsSection tableId={table.id} />

      {tableId && (
        <div className="my-4">
          <h3>Table Actions</h3>

          <table className="table-hover table-centered table-nowrap mb-5 table align-middle">
            <tbody>
              <AddFundsTable tableId={tableId} />
              <RemoveFundsTable tableId={tableId} />
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TableDetailsGame;
